<template>
  <div>
    <vue-headful :title="this.setPageTitle('monitoring')" />
    <loading :active="getLoading" :color="'blue'" is-full-page loader="spinner"> </loading>
    <b-container class="my-4">
      <!-- HEADER -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <h1 class="mb-0 h4 font-weight-normal">{{ $t('monitoring.title') }}</h1>
        </template>
        <template v-slot:right-content>
          <b-button size="sm" variant="link" @click="goToMonitoringHistory">
            {{ $t('monitoring.monitoringHistory') }}
          </b-button>
          <b-button
            size="sm"
            :variant="isMonitoringRunning ? 'warning' : 'primary'"
            @click="setUpMonitoring"
            :disabled="isMonitoringRunning"
          >
            {{ getButtonLabel }}
            <b-spinner small v-if="isMonitoringRunning"></b-spinner>
          </b-button>
        </template>
      </custom-flex-header>

      <!-- TABLE FILTERS -->
      <b-row class="mb-4">
        <b-col>
          <table-filters>
            <template v-slot:search>
              <label class="fieldLabel" for="name" v-t="'monitoring.monitoringsListTable.filterText'"> </label>
              <custom-search-bar
                :debounce="800"
                :text-value="getSearchBarValue"
                :placeholder="$t('monitoring.searchByMonitoring')"
                @on-search="onSearchMonitoring"
              >
              </custom-search-bar>
            </template>
            <template v-slot:filteredItems>
              <span class="text-secondary align-middle">
                <span class="font-weight-bold">{{ getPagination.items }}</span>
                {{ $tc('monitoring.monitoringsListTable.filteredMonitorings', getPagination.items) }}
              </span>
            </template>
            <template v-slot:resetButton>
              <div>
                <b-button @click="onResetFilters" variant="link" :disabled="!hasSearched" size="sm" class="ml-2">
                  <span>{{ $t('search.label.resetFilters') }} <i class="fa fa-trash small ml-1 icon-size-10"></i></span>
                </b-button>
              </div>
            </template>
          </table-filters>
        </b-col>
      </b-row>

      <template v-if="getMonitoringsList.length > 0">
        <b-row class="mb-4">
          <b-col>
            <div class="ml-auto">
              <!-- PAGINATION -->
              <table-pagination
                :pagination="getPagination"
                :pagination-options="pagination_options"
                :paginationText="getPaginationItemsCounter"
                :products-per-page="$t('monitoring.perPage')"
                :pagination-class="['mb-4']"
                @on-change-per-page="onChangePerPage"
                @on-change-current-page="onChangeCurrentPage"
              >
              </table-pagination>
            </div>
          </b-col>
        </b-row>

        <b-row class="mb-4">
          <b-col>
            <b-table
              class="mb-0"
              hover
              striped
              no-local-sorting
              no-sort-reset
              :sort-by.sync="defaultSortingKey"
              :sort-desc.sync="defaultSortDesc"
              :fields="fields"
              :items="getMonitoringsList"
              @sort-changed="onSortingChanged"
            >
              <template v-slot:cell(archived)="{ item }">
                <!-- archived -->
                <custom-tooltip
                  v-if="item.archived"
                  :index="item.id"
                  :is-font-awesome-icon="true"
                  :font-awesome-icon="'archive'"
                  :section="'monitoring-list-archived'"
                  :tooltip-text="$t('monitoring.monitoringsListTable.archivedTooltip')"
                >
                </custom-tooltip>
              </template>
              <template v-slot:cell(file)="{ item }">
                <!-- file -->
                <b-button
                  v-if="canExport || item.status !== 'Ended'"
                  size="sm"
                  :variant="item.status === 'Ended' ? 'outline-primary' : 'outline-secondary'"
                  :disabled="item.status !== 'Ended'"
                  @click="exportMonitoring(item.id)"
                >
                  <span v-if="item.status === 'Running'">
                    {{ $t('monitoring.monitoringsListTable.inProgress') }}
                  </span>
                  <span v-if="item.status === 'Ended'">
                    {{ $t('monitoring.monitoringsListTable.export') }}
                  </span>
                  <span v-if="item.status === 'Error'">
                    {{ $t('common.error') }}
                  </span>
                </b-button>
              </template>
              <template v-slot:cell(actions)="{ item }">
                <!-- actions -->
                <div class="text-right" v-if="item.status !== 'Running'">
                  <b-dropdown size="md" toggle-class="text-decoration-none button-primary-class p-3" right no-caret>
                    <template #button-content>
                      <i class="fas fa-ellipsis-v"></i>
                      <span class="sr-only">{{ $t('monitoring.monitoringsListTable.columns.actions') }}</span>
                    </template>
                    <b-dropdown-item-button @click="setUpRenameMonitoring(item.id, item.name)">
                      <small>{{ $t('monitoring.monitoringListActions.rename') }}</small>
                    </b-dropdown-item-button>
                    <b-dropdown-item-button
                      v-if="!item.archived && item.status !== 'Error'"
                      @click="monitoringArchive(item.id)"
                    >
                      <small>{{ $t('monitoring.monitoringListActions.archive') }}</small>
                    </b-dropdown-item-button>
                    <b-dropdown-item-button @click="monitoringDelete(item.id)">
                      <small>{{ $t('monitoring.monitoringListActions.delete') }}</small>
                    </b-dropdown-item-button>
                  </b-dropdown>
                </div>
              </template>
            </b-table>
          </b-col>
        </b-row>

        <!-- PAGINATION -->
        <b-row class="mb-4" v-if="getPagination.items > getPagination.perPage">
          <b-col cols="12">
            <div class="float-right">
              <b-pagination
                class="mb-0"
                :value="getPagination.currentPage"
                :total-rows="getPagination.items"
                :per-page="getPagination.perPage"
                @change="onChangeCurrentPage"
              >
              </b-pagination>
            </div>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <no-content
          :icon-name="'exclamation-circle'"
          :icon-class="['mb-3', 'fa-3x']"
          :text="$t('monitoring.noContent')"
        >
        </no-content>
      </template>
    </b-container>
    <simple-modal
      :title="modalTitle"
      :backLabel="$t('monitoring.monitoringModal.backLabel')"
      :confirm-label="confirmLabel"
      :id="modalId"
      show-title
      show-footer
      @start-monitoring-confirm-clicked="startMonitoring"
      @start-monitoring-back-clicked="closeModal"
      @rename-confirm-clicked="monitoringRename"
      @rename-back-clicked="closeModal"
    >
      <template #modal-content>
        <div class="mb-2">
          <div class="mb-2">{{ $t('common.name') }}</div>
          <b-input class="bg-white mb-2" size="sm" type="text" autocomplete="off" v-model="monitoringName"></b-input>
          <template v-if="modalId === 'start-monitoring'">
            <div class="mb-2">{{ $t('monitoring.monitoringModal.dateLabel') }}</div>
            <date-picker :value.sync="date" :disabled-dates="disabledDates" variant="white"></date-picker>
          </template>
        </div>
      </template>
    </simple-modal>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapActions, mapMutations } = createNamespacedHelpers('monitoringList')
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import CustomTooltip from '../../components/Lib/CustomTooltip.vue'
import TableFilters from '../../components/Lib/TableFilters.vue'
import CustomSearchBar from '../../components/ScoringProfiles/CustomSearchBar.vue'
import TablePagination from '../../components/ScoringProfiles/TablePagination.vue'
import SimpleModal from '../../components/Lib/SimpleModal.vue'
import DatePicker from '../../components/Lib/DatePicker.vue'
import NoContent from '../../components/Lib/NoContent.vue'
import { TODAY } from '../../settings/scoringProfilesSettings'
import { dateYearMonthDay, dateISOString } from '../../utils/formatter'
import { downloadFile } from './../../utils/fileManager'
import { hasExportPermission } from './../../utils/exportPermission'
import {
  FILE_TYPE,
  FILE_NAME_EXPORT_MONITORING,
  EXPORT_MONITORING_ENDPOINT,
} from './../../settings/scoringProfilesSettings'

export default {
  components: {
    CustomFlexHeader,
    TableFilters,
    CustomSearchBar,
    TablePagination,
    CustomTooltip,
    SimpleModal,
    DatePicker,
    NoContent,
  },
  data() {
    return {
      pagination_options: [25, 100, 500, 1000],
      date: null,
      monitoringName: null,
      modalTitle: null,
      modalId: null,
      idToRename: null,
      confirmLabel: null,
      isDownloading: false,
      isMonitoringRunning: false,
      disabledDates: {
        from: TODAY(),
      },
      defaultSortingKey: 'MonitoringDate',
      defaultSortDesc: true,
    }
  },
  computed: {
    ...mapGetters(['getPagination', 'getSearchBarValue', 'getMonitoringsList']),

    getPaginationItemsCounter: {
      get() {
        let counter = 1
        if (this.getPagination !== null && this.getPagination.items !== 0) {
          counter =
            this.getPagination.items <= this.getPagination.perPage
              ? 1
              : this.getPagination.perPage * (this.getPagination.currentPage - 1) + 1
        }

        return this.$t('monitoring.shown', {
          start: counter,
          end: Math.min(this.getPagination.perPage * this.getPagination.currentPage, this.getPagination.items),
          of: this.getPagination.items || 0,
        })
      },
    },

    canExport() {
      return hasExportPermission(this.$store.getters.getPermissions)
    },

    getLoading() {
      return this.$store.getters['scoringProfilesCommon/getLoading'] || this.isDownloading
    },

    hasSearched() {
      return this.getSearchBarValue.length > 0
    },

    isValidName() {
      return this.monitoringName && this.monitoringName.length > 0
    },

    getButtonLabel() {
      return this.isMonitoringRunning
        ? this.$t('monitoring.monitoringInProgress')
        : this.$t('monitoring.startMonitoring')
    },

    fields: {
      get() {
        return [
          {
            key: 'archived',
            class: ['archive', 'align-middle'],
            sortable: false,
            label: '',
          },
          {
            key: 'name',
            sortKey: 'Name',
            class: ['name', 'align-middle', 'font-weight-bold'],
            sortable: true,
            label: this.$t('monitoring.monitoringsListTable.columns.name'),
          },
          {
            key: 'launchedBy',
            sortKey: 'User',
            class: ['user', 'align-middle'],
            sortable: true,
            label: this.$t('monitoring.monitoringsListTable.columns.user'),
          },
          {
            key: 'monitoringDate',
            sortKey: this.defaultSortingKey,
            class: ['monitoring-date', 'align-middle', 'text-center'],
            formatter: (value) => {
              return value ? this.$d(new Date(value), 'short') : this.$t('common.notAvailable')
            },
            sortable: true,
            label: this.$t('monitoring.monitoringsListTable.columns.monitoringDate'),
          },
          {
            key: 'launchedAt',
            sortKey: 'Start',
            class: ['start-date', 'align-middle'],
            formatter: (value) => {
              return value ? this.$d(new Date(value), 'long') : this.$t('common.notAvailable')
            },
            sortable: true,
            label: this.$t('monitoring.monitoringsListTable.columns.startDate'),
          },
          {
            key: 'endedAt',
            sortKey: 'End',
            formatter: (value) => {
              return value ? this.$d(new Date(value), 'long') : this.$t('common.notAvailable')
            },
            class: ['end-date', 'align-middle'],
            sortable: true,
            label: this.$t('monitoring.monitoringsListTable.columns.endDate'),
          },
          {
            key: 'file',
            class: ['file', 'align-middle'],
            sortable: false,
            label: this.$t('monitoring.monitoringsListTable.columns.file'),
          },
          {
            key: 'actions',
            sortable: false,
            class: 'actions',
            label: this.$t('monitoring.monitoringsListTable.columns.actions'),
          },
        ]
      },
    },
  },
  methods: {
    ...mapMutations(['setSearchBarValue', 'setPaginationPerPage', 'setPaginationCurrentPage', 'setSortingValue']),
    ...mapActions([
      'loadMonitoringsList',
      'launchMonitoring',
      'archiveMonitoring',
      'deleteMonitoring',
      'renameMonitoring',
    ]),

    goToMonitoringHistory() {
      this.$router.push({ name: 'monitoringHistory' })
    },

    onSearchMonitoring(value) {
      this.setSearchBarValue(value)
      this.loadMonitoringsList()
    },

    setUpMonitoring() {
      this.modalTitle = this.$t('monitoring.monitoringModal.startMonitoring')
      this.modalId = 'start-monitoring'
      this.confirmLabel = this.$t('monitoring.monitoringModal.startLabel')
      this.date = TODAY()
      this.monitoringName = this.$t('monitoring.title') + ' ' + dateYearMonthDay(new Date().toJSON())
      // Wait for DOM update
      this.$nextTick(() => {
        this.$bvModal.show(this.modalId)
      })
    },

    setUpRenameMonitoring(id, name) {
      this.modalTitle = this.$t('monitoring.monitoringModal.renameMonitoring')
      this.modalId = 'rename'
      this.confirmLabel = this.$t('monitoring.monitoringModal.confirmLabel')
      this.idToRename = id
      this.monitoringName = name
      // Wait for DOM update
      this.$nextTick(() => {
        this.$bvModal.show(this.modalId)
      })
    },

    closeModal() {
      this.$bvModal.hide(this.modalId)
    },

    startMonitoring() {
      this.closeModal()

      if (this.isValidName) {
        this.isMonitoringRunning = true

        const payload = {
          monitoringDate: dateISOString(this.date).slice(0, 19),
          name: this.monitoringName,
        }

        this.launchMonitoring(payload)
          .then((data) => this.manageNotice(data))
          .catch(async (error) => {
            const err = await error.response
            const errorMessages = await err.text()

            // There could be more then an error in the string
            let firstMessage = errorMessages.split(';')[0]
            firstMessage = firstMessage.split('"').join('')

            this.manageErrorMessages(firstMessage)
          })
          .finally(() => {
            this.isMonitoringRunning = false
          })
      } else {
        this.displayToast(this.$t('monitoring.errorMessages.errorNoName'), 'danger')
      }
    },

    onResetFilters() {
      this.setSearchBarValue('')
      this.getPagination.currentPage === 1 ? this.loadMonitoringsList() : this.setPaginationCurrentPage(1)
    },

    onChangeCurrentPage(value) {
      this.setPaginationCurrentPage(value)
      this.loadMonitoringsList().catch((err) => {
        this.httpErrorsHandler(err)
      })
    },

    onChangePerPage(value) {
      this.setPaginationPerPage(value)
      this.loadMonitoringsList().catch((err) => {
        this.httpErrorsHandler(err)
      })
    },

    onSortingChanged(data) {
      let val = data.sortDesc ? '-' + data.sortBy : data.sortBy
      this.setSortingValue(val)
      this.getPagination.currentPage === 1 ? this.loadMonitoringsList() : this.setPaginationCurrentPage(1)
    },

    monitoringArchive(id) {
      this.$bvModal
        .msgBoxConfirm(this.$t('monitoring.monitoringModal.confirmArchive'), {
          cancelTitle: this.$t('common.cancel'),
        })
        .then((value) => {
          if (!value) return

          this.archiveMonitoring(id).catch(async (error) => {
            const err = await error.response
            const errorMessages = await err.text()

            // There could be more then an error in the string
            let firstMessage = errorMessages.split(';')[0]
            firstMessage = firstMessage.split('"').join('')

            this.manageErrorMessages(firstMessage)
          })
        })
    },

    monitoringDelete(id) {
      this.$bvModal
        .msgBoxConfirm(this.$t('monitoring.monitoringModal.confirmDelete'), {
          cancelTitle: this.$t('common.cancel'),
        })
        .then((value) => {
          if (!value) return

          this.deleteMonitoring(id).catch(async (error) => {
            const err = await error.response
            const errorMessages = await err.text()

            // There could be more then an error in the string
            let firstMessage = errorMessages.split(';')[0]
            firstMessage = firstMessage.split('"').join('')

            this.manageErrorMessages(firstMessage)
          })
        })
    },

    monitoringRename() {
      this.closeModal()

      const payload = {
        monitoringId: this.idToRename,
        name: this.monitoringName,
      }

      if (this.isValidName) {
        this.renameMonitoring(payload).catch(async (error) => {
          const err = await error.response
          const errorMessages = await err.text()

          // There could be more then an error in the string
          let firstMessage = errorMessages.split(';')[0]
          firstMessage = firstMessage.split('"').join('')

          this.manageErrorMessages(firstMessage)
        })
      } else {
        this.displayToast(this.$t('monitoring.errorMessages.errorNoName'), 'danger')
      }
    },

    // A launch that went through without monitoring anything is not an error: it says why.
    manageNotice(data) {
      switch (data && data.notice) {
        case 'NoProductsToMonitor':
          this.displayToast(this.$t('monitoring.notices.noProductsToMonitor'), 'info')
          break
        case 'NoProductsWithScoringProfile':
          this.displayToast(this.$t('monitoring.notices.noProductsWithScoringProfile'), 'info')
          break
      }
    },

    manageErrorMessages(message) {
      switch (message) {
        case 'BadMonitoringData':
          this.displayToast(this.$t('monitoring.errorMessages.errorBadMonitoringDate'), 'danger')
          break
        case 'NotFound':
          this.displayToast(this.$t('monitoring.errorMessages.errorNotFound'), 'danger')
          break
        case 'NameInUse':
          this.displayToast(this.$t('common.nameInUse'), 'danger')
          break
        case 'MonitoringIsRunning':
          this.displayToast(this.$t('monitoring.errorMessages.errorMonitoringAlreadyRunning'), 'danger')
          break
        default:
          this.$bvModal.msgBoxOk(this.$t('common.genericError'))
      }
    },

    displayToast(message, variant) {
      return this.$bvToast.toast(message, {
        title: this.$t('monitoring.title'),
        variant: variant,
      })
    },

    exportMonitoring(id) {
      if (!this.canExport) return
      this.isDownloading = true

      const payload = {
        monitoringId: id,
      }

      downloadFile(
        this,
        'post',
        EXPORT_MONITORING_ENDPOINT,
        payload,
        FILE_TYPE,
        'isDownloading',
        FILE_NAME_EXPORT_MONITORING,
        'Generic Error'
      )
    },
  },
  created() {
    this.loadMonitoringsList().catch((err) => this.httpErrorsHandler(err))
  },
}
</script>

<style lang="scss">
.page-link {
  background-color: #e9ecef;
}

.button-primary-class,
.button-primary-class:active {
  border: 0;
  color: var(--secondaryColor) !important;
  background-color: transparent !important;
}

.show > .button-primary-class {
  color: var(--secondaryColor) !important;
}

.page-item.disabled .page-link {
  background-color: #e9ecef;
}
</style>
